import isEmpty from "lodash.isempty";
import isEqual from "lodash.isequal";
import kebabCase from "lodash.kebabcase";
import { makeAutoObservable, runInAction } from "mobx";
import { enableStaticRendering } from "mobx-react";

import fetchVacancies from "../shared/helpers/fetchVacancies/fetchVacancies";

const isServer = typeof window === "undefined";
enableStaticRendering(isServer);

function isArrayKey(key) {
  const match = key.match(/(.+)\[(\d+)\]$/);
  return match ? { arrayName: match[1], index: parseInt(match[2], 10) } : null;
}

export const INIT_FILTERS = {
  category: null,
  companyId: null,
  distance: null,
  keywords: "",
  location: null,
  locationLabel: null,
  accountId: null,
  hoursType: null,
};

export const allowedFilters = Object.keys(INIT_FILTERS);

export const allowedBoxLinkFilters = ["group"];

export const allowedQueryFilters = [
  ...allowedFilters,
  ...allowedBoxLinkFilters,
];

const ACCEPTED_FILTERS = ["group", "category"];

const ACCEPTED_FRONTEND_FILTERS = ["category", "country", "hours", "location"];

// If there are multiple tenants then we have lost all pagination
const hasMultiTenant = process.env.EXTRA_TENANT_IDS?.length > 0;
const PER_PAGE = hasMultiTenant ? 5000 : 15;
export const SERVER_SIDE_FETCH_LIMIT = PER_PAGE;

class FiltersStore {
  filters = { ...INIT_FILTERS };

  sort = "newest";

  searchResults = [];

  searchBuckets = {};

  filtersMapping = {};

  appliedFilters = { ...this.filters };

  frontendFilters = {
    categories: [],
    country: [],
    location: [],
    hours: [],
    role: [],
  };

  hasFrontendFiltersBeenCleared = false;

  isFetching = false;

  pagination = {
    perPage: PER_PAGE,
    totalResults: 0,
  };

  // Search context — set once from getStaticProps, reused for all client-side searches
  searchContext = {
    locale: null,
    slug: null,
    predefinedFilters: null,
    tenantIds: null,
    isInternal: false,
    venueId: null,
  };

  constructor(initialData) {
    makeAutoObservable(this);
    if (!isEmpty(initialData)) {
      const {
        boxLinksMapping,
        searchResults,
        totalResults,
        searchContext,
        ...initialFilters
      } = initialData;
      this.filtersMapping = initialData?.boxLinksMapping;
      if (!isEmpty(initialFilters)) {
        this.filters = { ...initialFilters };
      }
      if (!isEmpty(initialFilters)) {
        this.appliedFilters = { ...initialFilters };
      }
      if (searchContext) {
        this.searchContext = { ...this.searchContext, ...searchContext };
      }
      this.setSearchResults(searchResults);
      this.setResultsPagination(totalResults);
    }
  }

  setSearchContext = (context) => {
    this.searchContext = { ...this.searchContext, ...context };
  };

  search = async (options = {}) => {
    const { order = "DESC", append = false, start = 0, limit } = options;
    const { locale, predefinedFilters, tenantIds, isInternal, venueId } =
      this.searchContext;

    this.isFetching = true;

    // Merge backend filters with frontend filters for the API query
    const query = { ...this.filters };
    if (this.frontendFilters.categories?.length > 0) {
      query.category = this.frontendFilters.categories.join(",");
    }
    if (this.frontendFilters.hours?.length > 0) {
      query.hoursType = this.frontendFilters.hours[0];
    }
    if (this.frontendFilters.location?.length > 0) {
      query.location = this.frontendFilters.location[0];
    }

    try {
      const additionalFilters = venueId ? { companyId: venueId } : undefined;
      const result = await fetchVacancies(
        query,
        locale,
        predefinedFilters,
        tenantIds?.split?.("\n")?.filter(Boolean) || tenantIds,
        additionalFilters,
        limit || this.pagination.perPage,
        start,
        isInternal,
        order
      );

      const vacancies = result?.data?.results || [];
      const buckets = result?.data?.buckets || {};
      const totalResults = result?.data?.totalResults || 0;

      runInAction(() => {
        if (append) {
          this.searchResults.push(...vacancies);
        } else {
          this.searchResults.replace(vacancies);
        }
        this.searchBuckets = buckets;
        this.pagination.totalResults = totalResults;
        this.isFetching = false;
      });

      return { vacancies, buckets, totalResults };
    } catch (error) {
      runInAction(() => {
        this.isFetching = false;
      });
      console.error("Search error:", error);
      return { vacancies: [], buckets: {}, totalResults: 0 };
    }
  };

  setFilters = (filters) => {
    this.filters = filters;
  };

  addCategoryToFilter = (newCategory) => {
    this.frontendFilters.categories.push(newCategory);
  };

  removeCategoryFromFilter = (categoryToRemove) => {
    this.frontendFilters.categories.remove(categoryToRemove);
  };

  setSearchResults = (results) => {
    this.searchResults.replace(results);
  };

  setSearchBuckets = (buckets) => {
    this.searchBuckets = buckets;
  };

  addSearchResults = (newResults) => {
    this.searchResults.push(...newResults);
  };

  setSort = (sort) => {
    this.sort = sort;
  };

  removeFromFilter = (type, propToRemove) => {
    this.frontendFilters[type].remove(propToRemove);
  };

  addFromFilter = (type, propToAdd) => {
    this.frontendFilters[type].push(propToAdd);
  };

  setFromFilter = (type, propToAdd) => {
    this.frontendFilters = {
      ...this.frontendFilters,
      [type]: propToAdd,
    };
  };

  get numberOfPages() {
    return (
      Math.ceil(
        (this.pagination.totalResults - SERVER_SIDE_FETCH_LIMIT) /
          this.pagination.perPage
      ) + 1
    );
  }

  setResultsPagination = (totalResults) => {
    this.pagination.totalResults = totalResults;
  };

  applyFilters = () => {
    Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
      this.appliedFilters[filterKey] = filterValue;
    });
  };

  setFiltersFromQuery = (query) => {
    const filtersToMap = Object.keys(query).filter((queryKey) =>
      ACCEPTED_FILTERS.includes(queryKey)
    );

    filtersToMap.forEach((filterKey) => {
      const filterValue = query[filterKey];
      if (filterValue) {
        const mappedFilterValue = this.filtersMapping[filterValue]?.filterValue;
        const mappedFilterName = this.filtersMapping[filterValue]?.filterName;
        if (mappedFilterValue && mappedFilterName) {
          this.appliedFilters[mappedFilterName] = mappedFilterValue;
        } else {
          this.filters = { ...INIT_FILTERS, [filterKey]: filterValue };
          this.applyFilters();
        }
      }
    });
  };

  setFrontendFiltersFromQuery = (query) => {
    const filtersToMap = Object.keys(query).filter(
      (queryKey) =>
        ACCEPTED_FRONTEND_FILTERS.includes(queryKey) || isArrayKey(queryKey)
    );

    filtersToMap.forEach((filterKey) => {
      const filterValue = query[filterKey];
      if (filterValue) {
        const arrayInfo = isArrayKey(filterKey);
        if (arrayInfo) {
          // Handle array-form keys
          const { arrayName } = arrayInfo;
          if (
            this.frontendFilters[arrayName] &&
            !this.frontendFilters[arrayName].includes(filterValue)
          ) {
            this.frontendFilters[arrayName].push(filterValue);
          }
        } else {
          const mappedFilterValue = (this.filtersMapping || {})[filterValue]
            ?.filterValue;
          const mappedFilterName = (this.filtersMapping || {})[filterValue]
            ?.filterName;
          if (mappedFilterValue && mappedFilterName) {
            this.appliedFilters[mappedFilterName] = mappedFilterValue;
          } else {
            this.filters = {
              ...ACCEPTED_FRONTEND_FILTERS,
              [filterKey]: filterValue,
            };
            this.applyFilters();
          }
        }
      }
    });
  };

  setFilter = (filterName, filterValue) => {
    this.filters[filterName] = filterValue;
  };

  setFiltersMapping = (filtersMapping) => {
    this.filtersMapping = filtersMapping;
  };

  resetAllFilters = () => {
    this.filters = { ...INIT_FILTERS };
    this.applyFilters();
    this.frontendFilters.role.replace([]);
    this.frontendFilters.categories.replace([]);
    this.frontendFilters.hours.replace([]);
    this.frontendFilters.location.replace([]);
    this.frontendFilters.country.replace([]);
    this.hasFrontendFiltersBeenCleared = true;
  };

  resetFilters = () => {
    this.filters = { ...INIT_FILTERS };
  };

  get categorySlug() {
    return kebabCase(this.filters.category) || "any-role";
  }

  get anyFiltersApplied() {
    return !isEqual(this.appliedFilters, INIT_FILTERS);
  }

  get isLocationSelected() {
    return (
      this.appliedFilters.location?.coords?.lat &&
      this.appliedFilters.location?.coords?.lng
    );
  }

  get vacancyCountByCategory() {
    const vacancyCountByCategory = this.searchResults.reduce((acc, result) => {
      acc[result.category] = acc[result.category]
        ? acc[result.category] + 1
        : 1;
      return acc;
    }, {});

    return vacancyCountByCategory;
  }

  get nonEmptyFilters() {
    const nonEmptyFiltersArray = Object.entries(this.filters).filter(
      ([, filterValue]) => !isEmpty(filterValue)
    );

    return Object.fromEntries(nonEmptyFiltersArray);
  }
}

export default FiltersStore;
